import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './Login.module.css';
import { FormikTextField } from '../../components/form-components/common-components/text-field/FormikTextField';
import { useStore } from '../../hooks/useStore';
import { LoginRequest } from '../../models/user/UserModels';
import { FormControlLabel, Checkbox, Box, IconButton, Alert } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import KaldenLogo from '../../assets/kalden-logo.png';
import { LoginSchema } from '../../schemas/LoginSchema';

export const Login = () => {
  const { authStore } = useStore();
  useEffect(() => {
    const rememberedUsername = authStore.getRememberedUsername();
    if (rememberedUsername) {
      setInitialValues(prev => ({
        ...prev,
        username: rememberedUsername,
        rememberMe: true,
      }));
    }
  }, [authStore]);

  const [initialValues, setInitialValues] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  useEffect(() => {
    const handleKeyEvent = (e: KeyboardEvent) => {
      if (e && typeof e.getModifierState === 'function') {
        setCapsLockOn(e.getModifierState('CapsLock'));
      }
    };
    window.addEventListener('keydown', handleKeyEvent);
    window.addEventListener('keyup', handleKeyEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyEvent);
      window.removeEventListener('keyup', handleKeyEvent);
    };
  }, []);

  const handleSubmit = async (values: LoginRequest & { rememberMe: boolean }) => {
    try {
      const { rememberMe, ...loginData } = values;
      const response = await authStore.login(loginData, rememberMe);

      if (response.error) {
        toast.error(response.message || 'Giriş başarısız');
        return;
      }
      if (response.value?.jwtToken) {
        toast.success(response.message || 'Giriş başarılı');
      } else {
        toast.error(response.message || 'Giriş başarısız');
      }
    } catch (error) {
      toast.error('Giriş işlemi sırasında bir hata oluştu');
    }
  };

  return (
    <div className={styles.container}>
      <img src={KaldenLogo} alt="Kalden Logo" className={styles.logo} />
      <div className={styles.formWrapper}>
        <h1 className={styles.title}>Giriş Yap</h1>
        <Formik
          initialValues={initialValues}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className={styles.form}>
              <FormikTextField
                name="username"
                label="Kullanıcı Adı"
                type="text"
                placeholder="Kullanıcı adınızı giriniz"
                disableUppercase
              />
              <FormikTextField
                name="password"
                label="Şifre"
                type={showPassword ? 'text' : 'password'}
                placeholder="Şifrenizi giriniz"
                disableUppercase
                endAdornment={
                  <IconButton
                    aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                    onClick={() => setShowPassword(prev => !prev)}
                    onMouseDown={e => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
              {capsLockOn && (
                <Alert severity="warning" className={styles.capsLockWarning}>
                  Caps Lock açık
                </Alert>
              )}
              <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                {isSubmitting ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
              </button>
              <Box className={styles.rememberMeContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.rememberMe}
                      onChange={e => setFieldValue('rememberMe', e.target.checked)}
                      name="rememberMe"
                      color="primary"
                    />
                  }
                  label="Beni Hatırla"
                />
              </Box>
              <div className={styles.registerLink}>
                Şifrenizi Mi Unuttunuz? <Link to="/forgot-password">Sıfırla</Link>
              </div>
              <div className={styles.registerLink}>
                Hesabınız yok mu? <Link to="/register">Kayıt Ol</Link>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
